import { useMemo } from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TutorialHeroArt } from './TutorialHeroArt';
import { TutorialPitchBackdrop } from './TutorialPitchBackdrop';
import { tutorialStyles } from '../../src/tutorial/styles/tutorial.styles';
import { getTutorialDynamicStyles } from '../../src/tutorial/helpers/tutorialDynamicStyles.helper';
import type { TutorialSlide } from '../../src/tutorial/types/tutorial.types';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';

interface TutorialItemProps {
  item: TutorialSlide;
  index: number;
  totalSlides: number;
  currentIndex: number;
  onSkip: () => void;
  onNext: () => void;
  onDone: () => void;
  /** Whether tutorial was triggered from menu (shows "Close" instead of "Skip") */
  isMenuTrigger?: boolean;
  /** True while the anonymous session is being created after "Get Started" — shows a spinner. */
  isBusy?: boolean;
  /** Test ID for testing */
  testID?: string;
}

export function TutorialItem({
  item,
  index,
  totalSlides,
  currentIndex,
  onSkip,
  onNext,
  onDone,
  isMenuTrigger = false,
  isBusy = false,
  testID,
}: TutorialItemProps) {
  const insets = useSafeAreaInsets();
  const dynamicStyles = useMemo(() => getTutorialDynamicStyles(insets), [insets]);
  const isLastSlide = index === totalSlides - 1;
  const isActive = currentIndex === index;
  // The last-slide "Get Started" kicks off guest sign-in; while it runs, show a spinner and lock the
  // slide's controls so a double-tap can't fire it twice.
  const showBusy = isLastSlide && isBusy;

  return (
    <View style={dynamicStyles.slideOuter} testID={testID || `tutorial-item-${index}`}>
      <View style={tutorialStyles.slideBackgroundFallback} pointerEvents="none" />
      <TutorialPitchBackdrop />

      <Pressable
        style={({ pressed, hovered }) => [
          dynamicStyles.skipButtonAbsolute,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && tutorialStyles.skipButtonPressed,
          hovered && Platform.OS === 'web' && tutorialStyles.skipButtonHoveredWeb,
        ]}
        onPress={onSkip}
        disabled={isBusy}
        testID={testID ? `${testID}-skip-button` : `tutorial-item-${index}-skip-button`}
      >
        <Text style={tutorialStyles.skipButtonText}>{isMenuTrigger ? 'Close' : 'Skip'}</Text>
      </Pressable>

      <View style={dynamicStyles.slideContent}>
        <TutorialHeroArt
          variant={item.visualVariant}
          isActive={isActive}
          headline={item.headline}
          subheadline={item.subheadline}
        />
      </View>

      <View style={dynamicStyles.bottomActions}>
        <Pressable
          style={({ pressed, hovered }) => [
            tutorialStyles.primaryButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && tutorialStyles.primaryButtonPressed,
            hovered && Platform.OS === 'web' && tutorialStyles.primaryButtonHoveredWeb,
          ]}
          onPress={isLastSlide ? onDone : onNext}
          disabled={showBusy}
          accessibilityState={{ busy: showBusy }}
          testID={
            testID
              ? `${testID}-${isLastSlide ? 'done' : 'next'}-button`
              : `tutorial-item-${index}-${isLastSlide ? 'done' : 'next'}-button`
          }
        >
          {showBusy ? (
            <ActivityIndicator color="#111827" />
          ) : (
            <Text style={tutorialStyles.primaryButtonText}>{isLastSlide ? 'Get Started' : 'Next'}</Text>
          )}
        </Pressable>

        <View style={tutorialStyles.paginationContainer}>
          {Array.from({ length: totalSlides }).map((_, dotIndex) => (
            <View
              key={dotIndex}
              style={[
                tutorialStyles.paginationDot,
                currentIndex === dotIndex && tutorialStyles.paginationDotActive,
              ]}
            />
          ))}
        </View>
      </View>
    </View>
  );
}
